import { useState, useEffect, useCallback } from 'react'
import { Trash2, Copy, ExternalLink, Tag, Shield, ShieldOff, X, Download } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function ImageDetail({ image, onDelete, setStatus }) {
  const { api } = useAuth()
  const [tbtvId, setTbtvId] = useState(null)
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [allTags, setAllTags] = useState([])
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)

  // Resolve content record and load tags
  const resolveAndLoadTags = useCallback(async () => {
    if (!image?.id) return
    try {
      const resolved = await api('/content/resolve', {
        method: 'POST',
        body: JSON.stringify({
          image_id: image.id,
          title: image.filename || image.id,
          content_type: 'image'
        })
      })
      setTbtvId(resolved.tbtv_id)

      const tagData = await api(`/tags/${resolved.tbtv_id}`)
      setTags(tagData.tags || [])
    } catch (e) {
      console.error('Failed to resolve image content:', e)
    }
  }, [image?.id, api])

  useEffect(() => {
    resolveAndLoadTags()
    loadAllTags()
  }, [resolveAndLoadTags])

  async function loadAllTags() {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }

  function addTag(tag) {
    const clean = tag.trim().toLowerCase()
    if (!clean || tags.includes(clean)) return
    setTags(prev => [...prev, clean])
    setTagInput('')
    setShowTagSuggestions(false)
  }

  function removeTag(tag) {
    setTags(prev => prev.filter(t => t !== tag))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  async function handleSaveTags() {
    if (!tbtvId) return
    setSaving(true)
    try {
      await api(`/tags/${tbtvId}`, {
        method: 'PUT',
        body: JSON.stringify({ tags })
      })
      setStatus('✅ Tags saved')
      loadAllTags()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  const filteredSuggestions = tagInput.length > 0
    ? allTags.filter(t => t.includes(tagInput.toLowerCase()) && !tags.includes(t))
    : []

  async function handleToggleSigned() {
    try {
      setStatus('Updating access...')
      await api(`/images/${image.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ requireSignedURLs: !image.requireSignedURLs })
      })
      setStatus(`✅ Signed URLs ${!image.requireSignedURLs ? 'enabled' : 'disabled'}`)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      setStatus('✅ Copied to clipboard')
    })
  }

  async function handleDownload(url) {
    if (!url) return
    setStatus('Downloading...')
    try {
      const res = await fetch(url)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      // Use filename from image, fallback to image id
      const filename = image.filename || `${image.id}.png`
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
      setStatus('✅ Download started')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  if (!image) return null

  // Build display URL from variants
  const publicUrl = image.variants?.find(v => v.endsWith('/public')) || image.variants?.[0] || ''
  const thumbnailUrl = image.variants?.find(v => v.endsWith('/thumbnail')) || publicUrl

  return (
    <div className="image-detail">
      <div className="image-detail-header">
        <h2>{image.filename || image.id}</h2>
        {tbtvId && <span className="tbtv-badge">{tbtvId}</span>}
      </div>

      {/* Preview */}
      <div className="image-detail-preview">
        {publicUrl ? (
          <img src={publicUrl} alt={image.filename || 'Image preview'} />
        ) : (
          <div className="image-detail-no-preview">No preview available</div>
        )}
      </div>

      {/* Metadata */}
      <div className="image-detail-meta">
        <div className="meta-row">
          <label>Image ID</label>
          <div className="meta-value">
            <code>{image.id}</code>
            <button className="icon-btn" onClick={() => copyToClipboard(image.id)} title="Copy ID">
              <Copy size={14} />
            </button>
          </div>
        </div>

        <div className="meta-row">
          <label>Filename</label>
          <span>{image.filename || '—'}</span>
        </div>

        <div className="meta-row">
          <label>Uploaded</label>
          <span>{image.uploaded ? new Date(image.uploaded).toLocaleString() : '—'}</span>
        </div>

        <div className="meta-row">
          <label>Signed URLs</label>
          <button className="toggle-btn" onClick={handleToggleSigned}>
            {image.requireSignedURLs
              ? <><Shield size={14} /> Required</>
              : <><ShieldOff size={14} /> Public</>
            }
          </button>
        </div>

        {publicUrl && (
          <div className="meta-row">
            <label>Public URL</label>
            <div className="meta-value">
              <code className="url-code">{publicUrl}</code>
              <button className="icon-btn" onClick={() => copyToClipboard(publicUrl)} title="Copy URL">
                <Copy size={14} />
              </button>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="icon-btn" title="Open">
                <ExternalLink size={14} />
              </a>
              <button className="icon-btn" onClick={() => handleDownload(publicUrl)} title="Download">
                <Download size={14} />
              </button>
            </div>
          </div>
        )}

        {/* All variant URLs */}
        {image.variants && image.variants.length > 1 && (
          <div className="meta-row meta-row-vertical">
            <label>Variants ({image.variants.length})</label>
            <div className="variant-list">
              {image.variants.map((v, i) => {
                const variantName = v.split('/').pop()
                return (
                  <div key={i} className="variant-item">
                    <span className="variant-name">{variantName}</span>
                    <button className="icon-btn" onClick={() => copyToClipboard(v)} title="Copy">
                      <Copy size={12} />
                    </button>
                    <a href={v} target="_blank" rel="noopener noreferrer" className="icon-btn" title="Open">
                      <ExternalLink size={12} />
                    </a>
                    <button className="icon-btn" onClick={() => handleDownload(v)} title="Download">
                      <Download size={12} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Custom metadata */}
        {image.meta && Object.keys(image.meta).length > 0 && (
          <div className="meta-row meta-row-vertical">
            <label>Custom Metadata</label>
            <pre className="meta-json">{JSON.stringify(image.meta, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="image-tags-section">
        <label>
          <Tag size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Tags
        </label>
        <div className="tags-input-wrap">
          <div className="tags-chip-list">
            {tags.map(tag => (
              <span key={tag} className="tag-chip">
                {tag}
                <button className="tag-remove" onClick={() => removeTag(tag)}><X size={12} /></button>
              </span>
            ))}
            <input
              type="text"
              className="tag-text-input"
              value={tagInput}
              onChange={e => {
                setTagInput(e.target.value)
                setShowTagSuggestions(true)
              }}
              onKeyDown={handleTagKeyDown}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
              placeholder={tags.length === 0 ? 'Add tags...' : ''}
            />
          </div>
          {showTagSuggestions && filteredSuggestions.length > 0 && (
            <div className="tag-suggestions">
              {filteredSuggestions.map(s => (
                <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={handleSaveTags} disabled={saving} style={{ marginTop: 8 }}>
          {saving ? 'Saving...' : 'Save Tags'}
        </button>
      </div>

      {/* Actions */}
      <div className="image-detail-actions">
        <button className="danger-btn" onClick={() => {
          if (confirm(`Delete "${image.filename || image.id}"?\n\nThis cannot be undone!`)) {
            onDelete(image.id)
          }
        }}>
          <Trash2 size={16} /> Delete Image
        </button>
      </div>
    </div>
  )
}
